/* The sheet gives this status no data block — `Text + create new MDF initiative button + go to
   Initiatives link + add justification button + remind PBM button` — so the body states the MDF
   situation and stops there. The prior-year MDF spend the cockpit also holds is deliberately not
   drawn: it belongs to the Initiatives tile's MDF sub-tab, and repeating it here would answer a
   question the status does not ask. */
import { ActionsPlanTypes } from 'legacy/lut/classes/helpers/partner360/usePartnerInfo'
import { ProgramTypes } from 'legacy/lut/classes/models/ProgramTypes'
import { KeyInitiativeEditor } from 'legacy/lut/components/widgets/public/partner360/actionsPlan/keyInitiatives/lpKeyInitiatives/components/keyInitiativeEditor/KeyInitiativeEditor'
import { selectProgramType } from 'legacy/lut/features/partner360/configuration'
import { useCallback, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'

import { Translation } from 'cng-common/features/translations'
import { INITIATIVES_SECTION_URL_VALUE, TAB_URL } from 'partner360/configs'
import { ATTENTION_STATUS_BITS } from 'partner360/entities/attentionStatuses'
import { p360TabsIds } from 'partner360/entities/partnerCompleteness'
import { scrollToElement } from 'partner360/helpers/scrollToElement'

import { FINANCIAL_COCKPIT_TRANSLATION_KEYS } from '../../../../configs/translations'
import { getCockpitFiscalYearLabel } from '../../../../lib'
import {
  selectEffectiveCockpitFYear,
  selectVisibleCockpitKeyInitiatives,
} from '../../../../model/selectors'
import type { ICockpitKeyInitiative } from '../../../../model/types'
import { useCockpitSectionNavigation } from '../../../components/cockpitSectionNavigation'
import { useAttentionDetailExit } from '../../hooks/useAttentionDetailExit'
import { AttentionStatusActions } from '../attentionStatusActions'

import styles from './AttentionStatusMdf.module.scss'

const KEYS = FINANCIAL_COCKPIT_TRANSLATION_KEYS.attentionStatuses.mdf

/** Where the Initiatives plan lives on the partner page, as the completeness table links to it. */
const INITIATIVES_URL_PARAMS = { [TAB_URL]: INITIATIVES_SECTION_URL_VALUE }

/**
 * An initiative counts as MDF-funded on its investment source alone — the same reading the
 * Initiatives tile's `mdfPlanned` figure is built on, minus its requirement of an amount: an
 * initiative flagged MDF with no sum typed in yet is still MDF the partner has planned, and
 * dropping it here would state a shortfall the plan does not have.
 */
const isMdfFunded = ({ HPInvestmentSource }: ICockpitKeyInitiative) =>
  HPInvestmentSource?.trim().toUpperCase() === ActionsPlanTypes.MDF.toUpperCase()

/**
 * The detail of the `MDF is not planned` status: how much MDF the year's plan actually carries,
 * and the standing fact that nobody can record why it carries none — the two things the sheet's
 * buttons act on.
 *
 * Both are read from the cockpit's own payload rather than recomputed: the condition behind the
 * status is evaluated on the server, and this section only shows the data behind it.
 */
export const AttentionStatusMdf = () => {
  const { isRemote, openSection } = useCockpitSectionNavigation()
  const fYear = useSelector(selectEffectiveCockpitFYear)
  const initiatives = useSelector(selectVisibleCockpitKeyInitiatives)
  const programType = useSelector(selectProgramType)

  /* The editor is mounted only once it is asked for: it fetches the plan's financials and the
     JBP settings on mount, and a status card is not the place to pay for a dialog nobody opened.
     The initiatives table mounts it the same way, behind its own `RenderWhen`. */
  const [isCreating, setIsCreating] = useState(false)

  const yearLabel = getCockpitFiscalYearLabel(fYear)

  /* The status is not scoped to an offering — the sheet's `Per offering` column says no — so the
     whole year's plan is counted, whichever offering each initiative belongs to. */
  const yearInitiatives = useMemo(
    () => initiatives.filter(({ FYear }) => FYear === fYear),
    [initiatives, fYear]
  )

  const mdfInitiatives = useMemo(() => yearInitiatives.filter(isMdfFunded), [yearInitiatives])

  const exitDetail = useAttentionDetailExit()

  const handleGoToInitiatives = useCallback(() => {
    scrollToElement(p360TabsIds)
    openSection(INITIATIVES_URL_PARAMS)
    exitDetail()
  }, [exitDetail, openSection])

  /* The editor is the partner page's legacy dialog and reads that page's own partner, so away from
     the page the initiative is created on the partner's Initiatives tab instead. */
  const handleCreateInitiative = useCallback(() => {
    if (isRemote) {
      openSection(INITIATIVES_URL_PARAMS)
      return
    }

    setIsCreating(true)
  }, [isRemote, openSection])

  const handleCloseEditor = useCallback(() => setIsCreating(false), [])

  return (
    <div className={styles.root}>
      {/* The count on its own plate, the way the completeness section states its total: the number
        carries the plate, and the line under it puts the number back into the plan it came from —
        a bare `0` reads as a failed load rather than as a plan without MDF. */}
      <div className={styles.plan}>
        <div className={styles.count}>
          <span className={styles.countValue}>{mdfInitiatives.length}</span>
          <span className={styles.countLabel}>
            <Translation
              span
              text={KEYS.initiatives}
              replaceParams={[{ key: '{year}', value: yearLabel }]}
            />
          </span>
        </div>

        <p className={styles.hint}>
          {yearInitiatives.length === 0 ? (
            <Translation
              span
              text={KEYS.noPlan}
              replaceParams={[{ key: '{year}', value: yearLabel }]}
            />
          ) : (
            <Translation
              span
              text={KEYS.ofTotal}
              replaceParams={[{ key: '{total}', value: String(yearInitiatives.length) }]}
            />
          )}
        </p>
      </div>

      {/* The sheet's second button acts on a justification and nothing in the cockpit stores one —
        the plan review keeps its justifications in the review session until a persistence
        procedure exists. The card says that in a sentence rather than drawing a labelled row whose
        value could never read anything but the same constant. */}
      <p className={styles.note}>
        <Translation span text={KEYS.justificationNote} />
      </p>

      {/* `addJustification` is drawn with no call behind it: it has no procedure to reach yet.
        `remindPbm` is the card's own reminder. */}
      <AttentionStatusActions
        bitValue={ATTENTION_STATUS_BITS.mdf}
        className={styles.actions}
        handlers={{
          createInitiative: handleCreateInitiative,
          goToSection: handleGoToInitiatives,
        }}
      />

      {isCreating && (
        <KeyInitiativeEditor
          /* The button says `Create new MDF initiative`, so the form opens on MDF rather than on
             an empty source the PBM has to find again. */
          initialState={{ HPInvestmentSource: ActionsPlanTypes.MDF, HPInvestmentSum: 0 }}
          isDialogOpened
          isDataInsightPresent={programType !== ProgramTypes.Retail}
          onEditCancel={handleCloseEditor}
          onUpdateFinished={handleCloseEditor}
        />
      )}
    </div>
  )
}

AttentionStatusMdf.displayName = 'AttentionStatusMdf'
